<template>
  <div>
    <button
      class="ppt-btn"
      @click="showDateRangeModal = true"
    >
      <i class="fa fa-file-powerpoint-o mr-2" />
      PPTX
    </button>

    <BaseModal
      :show="showDateRangeModal"
      title="Selecciona un Rango de Tiempo"
      confirm-text="Generar"
      confirm-variant="primary"
      @confirm="onDatesConfirmed"
      @update:show="showDateRangeModal = $event"
    >
      <div class="export-type-selector">
        <button
          :class="['export-type-btn', exportType === 'pptx' ? 'active' : '']"
          type="button"
          @click="exportType = 'pptx'"
        >
          <i class="fa fa-file-powerpoint-o mr-1" />PPTX
        </button>
        <button
          :class="['export-type-btn', exportType === 'xlsx' ? 'active' : '']"
          type="button"
          @click="exportType = 'xlsx'"
        >
          <i class="fa fa-file-excel-o mr-1" />XLSX
        </button>
      </div>

      <b-form-group label="Ordenar por:">
        <b-form-select
          v-model="selectedOrder"
          :options="sortOrderOptions"
        />
      </b-form-group>

      <div class="date-range-row">
        <div class="date-group">
          <div class="date-group-label">
            Desde
          </div>
          <div class="date-inputs-group">
            <div class="input-group-item">
              <label
                class="input-label"
                for="from-year"
              >Año</label>
              <select
                id="from-year"
                v-model="fromYear"
                class="modern-select date-input"
              >
                <option
                  v-for="y in years"
                  :key="y"
                  :value="y"
                >
                  {{ y }}
                </option>
              </select>
            </div>
            <div class="input-group-item">
              <label
                class="input-label"
                for="from-month"
              >Mes</label>
              <select
                id="from-month"
                v-model="fromMonth"
                class="modern-select date-input"
              >
                <option
                  v-for="m in months"
                  :key="m.value"
                  :value="m.value"
                >
                  {{ m.label }}
                </option>
              </select>
            </div>
            <div class="input-group-item">
              <label
                class="input-label"
                for="from-day"
              >Día</label>
              <select
                id="from-day"
                v-model="fromDay"
                class="modern-select date-input"
              >
                <option
                  v-for="d in fromDaysInMonth"
                  :key="d"
                  :value="d"
                >
                  {{ d }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="date-range-sep">
          <i class="fa fa-long-arrow-right" />
        </div>

        <div class="date-group">
          <div class="date-group-label">
            Hasta
          </div>
          <div class="date-inputs-group">
            <div class="input-group-item">
              <label
                class="input-label"
                for="to-year"
              >Año</label>
              <select
                id="to-year"
                v-model="toYear"
                class="modern-select date-input"
              >
                <option
                  v-for="y in years"
                  :key="y"
                  :value="y"
                >
                  {{ y }}
                </option>
              </select>
            </div>
            <div class="input-group-item">
              <label
                class="input-label"
                for="to-month"
              >Mes</label>
              <select
                id="to-month"
                v-model="toMonth"
                class="modern-select date-input"
              >
                <option
                  v-for="m in months"
                  :key="m.value"
                  :value="m.value"
                >
                  {{ m.label }}
                </option>
              </select>
            </div>
            <div class="input-group-item">
              <label
                class="input-label"
                for="to-day"
              >Día</label>
              <select
                id="to-day"
                v-model="toDay"
                class="modern-select date-input"
              >
                <option
                  v-for="d in toDaysInMonth"
                  :key="d"
                  :value="d"
                >
                  {{ d }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="dateError"
        class="date-error"
      >
        <i class="fa fa-exclamation-circle mr-1" />{{ dateError }}
      </div>
    </BaseModal>

    <!-- Oculto visualmente; el modal de progreso y floating card siguen funcionando -->
    <executor-run-button
      ref="executorBtn"
      executor-name="NEWS_FEED_PPTX_DOWNLOAD"
      :request-payload="pptPayload"
      icon="fa-file-powerpoint-o"
      tooltip="Generar PPTX"
      class="d-none"
    />
    <executor-run-button
      ref="executorXlsxBtn"
      executor-name="NEWS_FEED_XLSX_DOWNLOAD"
      :request-payload="pptPayload"
      icon="fa-file-excel-o"
      tooltip="Generar XLSX"
      class="d-none"
    />
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import moment from 'moment'
import BaseModal from '@/app/shared/components/BaseModal.vue'
import ExecutorRunButton from '@/app/shared/components/ExecutorRunButton.vue'

const MONTHS = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' }, { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' }, { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
]

const currentYear = moment().year()
const YEARS = Array.from({ length: currentYear - 2022 }, (_, i) => 2023 + i)

const daysInMonth = (year, month) => moment(`${year}-${month}`, 'YYYY-M').daysInMonth()

export default {
  name: 'ExportablePpt',
  components: { BaseModal, ExecutorRunButton },
  setup (props, context) {
    const executorBtn = ref(null)
    const executorXlsxBtn = ref(null)
    const showDateRangeModal = ref(false)
    const pptPayload = ref({})
    const dateError = ref('')
    const exportType = ref('pptx')

    const selectedOrder = ref('created_at_descending')
    const sortOrderOptions = [
      { value: 'created_at_descending', text: 'Fecha descendiente' },
      { value: 'created_at_ascending', text: 'Fecha ascendente' },
      { value: 'retail', text: 'Retail' },
      { value: 'store', text: 'Tienda' }
    ]

    const prevMonday = moment().subtract(1, 'weeks').startOf('isoWeek')
    const prevSunday = moment().subtract(1, 'weeks').endOf('isoWeek')

    const fromYear = ref(prevMonday.year())
    const fromMonth = ref(prevMonday.month() + 1)
    const fromDay = ref(prevMonday.date())

    const toYear = ref(prevSunday.year())
    const toMonth = ref(prevSunday.month() + 1)
    const toDay = ref(prevSunday.date())

    const fromDaysInMonth = computed(() => {
      const n = daysInMonth(fromYear.value, fromMonth.value)
      return Array.from({ length: n }, (_, i) => i + 1)
    })

    const toDaysInMonth = computed(() => {
      const n = daysInMonth(toYear.value, toMonth.value)
      return Array.from({ length: n }, (_, i) => i + 1)
    })

    // Clamp day when month/year changes and current day exceeds new max
    watch(fromDaysInMonth, (days) => {
      if (fromDay.value > days.length) fromDay.value = days.length
    })
    watch(toDaysInMonth, (days) => {
      if (toDay.value > days.length) toDay.value = days.length
    })

    const onDatesConfirmed = async () => {
      const start = moment(`${fromYear.value}-${fromMonth.value}-${fromDay.value}`, 'YYYY-M-D')
      const end = moment(`${toYear.value}-${toMonth.value}-${toDay.value}`, 'YYYY-M-D')
      if (end.isBefore(start)) {
        dateError.value = 'La fecha "Hasta" no puede ser anterior a "Desde"'
        return
      }
      dateError.value = ''
      showDateRangeModal.value = false
      pptPayload.value = {
        start_date: start.format('YYYY-MM-DD'),
        end_date: end.format('YYYY-MM-DD'),
        order_by: selectedOrder.value
      }
      await context.root.$nextTick()
      if (exportType.value === 'xlsx') {
        if (executorXlsxBtn.value) executorXlsxBtn.value.handleClick()
      } else {
        if (executorBtn.value) executorBtn.value.handleClick()
      }
    }

    return {
      executorBtn,
      executorXlsxBtn,
      exportType,
      showDateRangeModal,
      pptPayload,
      dateError,
      years: YEARS,
      months: MONTHS,
      selectedOrder,
      sortOrderOptions,
      fromYear,
      fromMonth,
      fromDay,
      toYear,
      toMonth,
      toDay,
      fromDaysInMonth,
      toDaysInMonth,
      onDatesConfirmed
    }
  }
}
</script>

<style scoped>
.ppt-btn {
  display: inline-flex;
  align-items: center;
  background: #edf2f7;
  color: #4a5568;
  border: none;
  border-radius: 20px;
  padding: 0.38rem 0.9rem;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  line-height: 1;
}

.ppt-btn:hover {
  background: #e2e8f0;
}

.date-range-row {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  margin-top: 0.5rem;
}

.date-group {
  flex: 1;
}

.date-group-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.4rem;
}

.date-inputs-group {
  display: flex;
  gap: 0.5rem;
}

.input-group-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
}

.input-label {
  font-size: 0.7rem;
  color: #a0aec0;
  margin-bottom: 0;
}

.modern-select {
  appearance: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #2d3748;
  font-size: 0.82rem;
  padding: 0.35rem 0.5rem;
  width: 100%;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.modern-select:focus {
  outline: none;
  border-color: #4299e1;
  background: #fff;
}

.date-range-sep {
  color: #a0aec0;
  font-size: 1.1rem;
  padding-bottom: 0.5rem;
  flex-shrink: 0;
}

.date-error {
  margin-top: 0.6rem;
  font-size: 0.8rem;
  color: #e53e3e;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 0.4rem 0.7rem;
}

.export-type-selector {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.export-type-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #edf2f7;
  color: #4a5568;
  border: 2px solid transparent;
  border-radius: 20px;
  padding: 0.4rem 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.export-type-btn:hover {
  background: #e2e8f0;
}

.export-type-btn.active {
  background: #ebf8ff;
  border-color: #4299e1;
  color: #2b6cb0;
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/components/ExportablePpt.vue